import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Tabs from '../components/tabs/Tabs';
import Spinner from '../components/Spinner/spinner';
import Image from '../components/image/Image';
import Icon from '../components/icon/Icon';
import { getApprovedLines, getOngoingMonetization } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { formatCuit } from '../utils/Masks';

interface TableRow {
  application_number: number;
  application_product_line_id: number;
  product_line_code: string;
  cuit: string;
  product_line_label: string;
  id_currency: number;
  currency_label: string;
  approved_amount: number;
  monetization_amount: number;
  available_amount: number;
  company_name: string;
}

interface MonetizationRow {
  id: number;
  id_global: string;
  cuit: string;
  company_name: string;
  product_line_label: string;
  creditor: string;
  issue_date: string;
  due_date: string;
  currency_label: string;
  monetization_amount: number;
}

const Monetization = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [itemsPerPage] = useState<number>(10);
  const [tablePaginationData, setTablePaginationData] = useState<any>(null);
  
  // Check URL params for tab selection
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get('tab');
  const [activeTab, setActiveTab] = useState<number>(tabParam ? parseInt(tabParam) : 0);

  // Estados para el tab "En gestión"
  const [ongoingSearchText, setOngoingSearchText] = useState<string>('');
  const [isOngoingSearching, setIsOngoingSearching] = useState<boolean>(false);
  const [hasOngoingSearched, setHasOngoingSearched] = useState<boolean>(false);
  const [ongoingTableData, setOngoingTableData] = useState<MonetizationRow[]>([]);
  const [ongoingCurrentPage, setOngoingCurrentPage] = useState<number>(1);
  const [ongoingPaginationData, setOngoingPaginationData] = useState<any>(null);

  // Cargar datos automáticamente cuando se cambia al tab "En gestión"
  useEffect(() => {
    if (activeTab === 1 && !hasOngoingSearched) {
      loadOngoingData();
    }
  }, [activeTab]);

  const loadOngoingData = async () => {
    setIsOngoingSearching(true);
    setHasOngoingSearched(true);

    try {
      const response = await getOngoingMonetization('', ongoingCurrentPage, itemsPerPage);
      
      if (response.success && 'data' in response) {
        const data = response.data;
        setOngoingTableData(data.content || []);
        setOngoingPaginationData({
          size: data.size,
          page: ongoingCurrentPage - 1, // Convert to 0-based for Table component
          totalElements: data.total_elements,
          totalPages: data.total_pages,
        });
      } else {
        setOngoingTableData([]);
        setOngoingPaginationData(null);
      }
    } catch (error) {
      console.error('Error fetching ongoing monetization:', error);
      setOngoingTableData([]);
      setOngoingPaginationData(null);
    } finally {
      setIsOngoingSearching(false);
    }
  };

  const formatNumber = (amount: number) => {
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getCurrencyName = (currencyLabel: string) => {
    const currencyMap: { [key: string]: string } = {
      'ARS': 'Pesos argentinos',
      'USD': 'Dólares',
    };
    return currencyMap[currencyLabel] || currencyLabel;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleSearch = async (keyCode?: number) => {
    // Only search when Enter key is pressed or button is clicked
    if (keyCode && keyCode !== 13) {
      return;
    }

    if (!searchText.trim()) {
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      // Remove hyphens from CUIT if present
      let processedSearchText = searchText.trim();
      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedSearchText)) {
        processedSearchText = processedSearchText.replace(/-/g, '');
      }

      const response = await getApprovedLines(processedSearchText, currentPage, itemsPerPage);
      
      if (response.success && 'data' in response) {
        const data = response.data;
        
        // Use the product_lines array directly from the new response
        setTableData(data.product_lines || []);
        setTablePaginationData({
          size: data.size,
          page: data.page,
          totalElements: data.total_elements,
          totalPages: data.total_pages,
        });
        setCurrentPage(data.page || 0);
      } else {
        setTableData([]);
        setTablePaginationData(null);
      }
    } catch (error) {
      console.error('Error fetching approved lines:', error);
      setTableData([]);
      setTablePaginationData(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePlusClick = (row: TableRow) => {
    navigate(`${routes.newMonetization}?product_line_id=${row.application_product_line_id}&from=monetization`);
  };

  const handleRowClicked = (row: TableRow) => {
    navigate(`${routes.newMonetization}?product_line_id=${row.application_product_line_id}&from=monetization`);
  };

  // Función para buscar en "En gestión"
  const handleOngoingSearch = async (keyCode?: number) => {
    // Only search when Enter key is pressed or button is clicked
    if (keyCode && keyCode !== 13) {
      return;
    }

    if (!ongoingSearchText.trim()) {
      // Si no hay texto, cargar todos los datos
      await loadOngoingData();
      return;
    }

    setIsOngoingSearching(true);
    setHasOngoingSearched(true);

    try {
      const response = await getOngoingMonetization(ongoingSearchText, ongoingCurrentPage, itemsPerPage);
      
      if (response.success && 'data' in response) {
        const data = response.data;
        setOngoingTableData(data.content || []);
        setOngoingPaginationData({
          size: data.size,
          page: ongoingCurrentPage - 1, // Convert to 0-based for Table component
          totalElements: data.total_elements,
          totalPages: data.total_pages,
        });
      } else {
        setOngoingTableData([]);
        setOngoingPaginationData(null);
      }
    } catch (error) {
      console.error('Error fetching ongoing monetization:', error);
      setOngoingTableData([]);
      setOngoingPaginationData(null);
    } finally {
      setIsOngoingSearching(false);
    }
  };

  const handleOngoingChangePage = (newPage: number) => {
    // newPage comes from Table component (0-based), convert to 1-based for our logic
    const actualPage = newPage + 1;
    if (ongoingPaginationData && actualPage >= 1 && actualPage <= ongoingPaginationData.totalPages) {
      setOngoingCurrentPage(actualPage);
      // Re-fetch data with new page
      setIsOngoingSearching(true);
      const searchText = ongoingSearchText.trim();
      getOngoingMonetization(searchText, actualPage, itemsPerPage)
        .then((response) => {
          if (response.success && 'data' in response) {
            const data = response.data;
            setOngoingTableData(data.content || []);
            setOngoingPaginationData({
              size: data.size,
              page: newPage, // newPage is already 0-based from Table component
              totalElements: data.total_elements,
              totalPages: data.total_pages,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching ongoing monetization:', error);
        })
        .finally(() => {
          setIsOngoingSearching(false);
        });
    }
  };

  const handleOngoingRowClick = (row: MonetizationRow) => {
    // Determinar el tipo de operación basado en el product_line_label
    const getOperationType = (productLineLabel: string) => {
      const label = productLineLabel.toLowerCase();
      
      if (label.includes('cdp') || label.includes('cheque')) {
        return 'cheque';
      }
      
      if (label.includes('pagaré') || label.includes('pagare')) {
        return 'pagaré';
      }
      
      if (label.includes('préstamo') || label.includes('prestamo') || label.includes('operaciones públicas')) {
        return 'préstamo';
      }
      
      if (label.includes('aval comercial')) {
        return 'commercial-endorsements';
      }
      
      return 'cheque'; // default
    };

    const operationType = getOperationType(row.product_line_label);
    
    // Navegar a la vista de edición con los parámetros necesarios
    navigate(`/monetizacion/edit/${row.id_global}?operation_type=${operationType}`);
  };

  // Columnas para la tabla de "En gestión"
  const ongoingColumns = [
    {
      name: 'Nº Aval',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.id_global}
          </P>
        </Card>
      ),
      width: '7%',
    },
    {
      name: 'CUIT',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatCuit(row.cuit)}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Razón social',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.company_name}
          </P>
        </Card>
      ),
      width: '15%',
    },
    {
      name: 'Instrumento',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.product_line_label}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Acreedor',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.creditor}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'F. emisión',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatDate(row.issue_date)}
          </P>
        </Card>
      ),
      width: '10%',
    },
    {
      name: 'F. vencimiento',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatDate(row.due_date)}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Divisa',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.currency_label}
          </P>
        </Card>
      ),
      width: '11%',
    },
    {
      name: 'Bruto',
      selector: (row: MonetizationRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.monetization_amount)}
          </P>
        </Card>
      ),
      width: '8%',
    },
    {
      name: '',
      selector: (row: MonetizationRow) => (
        <Card>
          <Icon 
            name='arrow_right_outline' 
            variant='info'
            onClick={() => handleOngoingRowClick(row)}
          />
        </Card>
      ),
      width: '5%',
      minWidth: '60px',
      center: true,
      ignoreRowClick: true,
    },
  ];

  const columns = [
    {
      name: 'CUIT',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatCuit(row.cuit)}
          </P>
        </Card>
      ),
      width: '10%',
    },
    {
      name: 'Razón social',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.company_name}
          </P>
        </Card>
      ),
      width: '16%',
    },
    {
      name: 'Línea',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.application_product_line_id}
          </P>
        </Card>
      ),
      width: '7%',
    },
    {
      name: 'Instrumento',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.product_line_label}
          </P>
        </Card>
      ),
      width: '13%',
    },
    {
      name: 'Divisa',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {getCurrencyName(row.currency_label)}
          </P>
        </Card>
      ),
      width: '12%',
    },
    {
      name: 'Total',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.approved_amount)}
          </P>
        </Card>
      ),
      width: '12%',
    },
    {
      name: 'Utilizado',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.monetization_amount)}
          </P>
        </Card>
      ),
      width: '12%',
    },
    {
      name: 'Disponible',
      selector: (row: TableRow) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.available_amount)}
          </P>
        </Card>
      ),
      width: '13%',
    },
    {
      cell: (row: TableRow) => {
        const isDisabled = Number(row.available_amount) < 0.01;
        return (
          <Icon 
            name='plus_icon' 
            variant={isDisabled ? 'neutral' : 'info'}
            modifier={isDisabled ? 'weak' : undefined}
            onClick={isDisabled ? undefined : () => handlePlusClick(row)}
          />
        );
      },
      button: true,
      width: '5%',
      minWidth: '80px',
      center: true,
      ignoreRowClick: true,
    },
  ];

  // Pagination - now handled by server
  const handleChangePage = (newPage: number) => {
    if (tablePaginationData && newPage >= 0 && newPage < tablePaginationData.totalPages) {
      setCurrentPage(newPage);
      // Re-fetch data with new page
      setIsSearching(true);
      let processedSearchText = searchText.trim();
      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedSearchText)) {
        processedSearchText = processedSearchText.replace(/-/g, '');
      }
      
      getApprovedLines(processedSearchText, newPage, itemsPerPage)
        .then((response) => {
          if (response.success && 'data' in response) {
            const data = response.data;
            setTableData(data.product_lines || []);
            setTablePaginationData({
              size: data.size,
              page: data.page,
              totalElements: data.total_elements,
              totalPages: data.total_pages,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching approved lines:', error);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  };

  const tabs = [
    {
      label: 'Nueva solicitud',
      content: (
        <>
          {/* Search Section */}
          <Card width='400px' display='flex' gap='16px' margin={{ bottom: '3' }}>
            <Input
              type='text'
              size='md'
              icons={{ iconLeft: 'search_outline' }}
              placeholder='Buscar cliente'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyup={(e) => handleSearch(e.keyCode)}
            />
            <Button variant='tertiary' size='sm' onClick={() => handleSearch(13)}>
              {isSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
            </Button>
          </Card>

          {/* Table Section */}
          {isSearching ? (
            <Card margin={{ top: '4' }}>
              <TableSkeleton rows={10} columns={columns.length} />
            </Card>
          ) : hasSearched && tableData.length > 0 ? (
            <Card margin={{ top: '2' }} justifyContent='center' alignItems='center'>
              <Table
                columns={columns}
                data={tableData}
                tableData={tablePaginationData}
                handleChangePage={(page: number) => handleChangePage(page)}
                onRowClicked={handleRowClicked}
                variant='normal'
              />
            </Card>
          ) : hasSearched && tableData.length === 0 ? (
            <Card
              display='grid'
              justifyContent='center'
              margin={{ top: '11', bottom: '11' }}
              width='53vw'
            >
              <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
                <Image name='folder_empty' />
              </Card>
              <P variant='primary' align='center' withMargin={false} weight='bold'>
                No encontramos resultados.
              </P>
              <P variant='primary' align='center'>
                Podés hacer una nueva búsqueda.
              </P>
            </Card>
          ) : null}
        </>
      ),
    },
    {
      label: 'En gestión',
      content: (
        <>
          {/* Search Section */}
          <Card width='400px' display='flex' gap='16px' margin={{ bottom: '3' }}>
            <Input
              type='text'
              size='md'
              icons={{ iconLeft: 'search_outline' }}
              placeholder='Buscar cliente'
              value={ongoingSearchText}
              onChange={(e) => setOngoingSearchText(e.target.value)}
              onKeyup={(e) => handleOngoingSearch(e.keyCode)}
            />
            <Button variant='tertiary' size='sm' onClick={() => handleOngoingSearch(13)}>
              {isOngoingSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
            </Button>
          </Card>

          {/* Table Section */}
          {isOngoingSearching ? (
            <Card margin={{ top: '4' }}>
              <TableSkeleton rows={10} columns={ongoingColumns.length} />
            </Card>
          ) : hasOngoingSearched && ongoingTableData.length > 0 ? (
            <Card margin={{ top: '2' }} justifyContent='center' alignItems='center'>
              <Table
                columns={ongoingColumns}
                data={ongoingTableData}
                tableData={ongoingPaginationData}
                handleChangePage={(page: number) => handleOngoingChangePage(page)}
                onRowClicked={handleOngoingRowClick}
                variant='normal'
              />
            </Card>
          ) : hasOngoingSearched && ongoingTableData.length === 0 ? (
            <Card
              display='grid'
              justifyContent='center'
              margin={{ top: '11', bottom: '11' }}
              width='53vw'
            >
              <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
                <Image name='folder_empty' />
              </Card>
              <P variant='primary' align='center' withMargin={false} weight='bold'>
                No encontramos resultados.
              </P>
              <P variant='primary' align='center'>
                Podés hacer una nueva búsqueda.
              </P>
            </Card>
          ) : null}
        </>
      ),
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Monetización
          </H>
        </Card>
      </Card>

      <Tabs 
        tabs={tabs} 
        defaultActiveTab={0}
        selectedTab={activeTab}
        onTabChange={setActiveTab}
      />
    </Card>
  );
};

export default Monetization;
